<script setup>
import axios from 'axios';
import _ from 'lodash';
import { inject, nextTick, onMounted, ref, reactive } from 'vue';
import { deepArrObjDiff } from '../../helpers/helper';
import Loading from '../Loading.vue';
import Modal from '../Modal.vue';
import PageHeader from "../PageHeader.vue";
import Dropdown from '../UI/Dropdown.vue';
import FormInputOld from '../forms/FormInput.vue';
import IconCaretDown from '../icons/IconCaretDown.vue';
import IconCaretUp from '../icons/IconCaretUp.vue';
import IconClose from '../icons/IconClose.vue';
import IconEllipsis from '../icons/IconEllipsis.vue';
import IconEyeOpen from '../icons/IconEyeOpen.vue';
import IconTrash from '../icons/IconTrash.vue';
import Login from '../preview/Login.vue';
import PersonalDetails from '../preview/PersonalDetails.vue';
import Registration from '../preview/Registration.vue';
import FormButton from './../../pge-components/pge-button.vue';
import DropdownMenu from './../../pge-components/pge-dropdown/pge-dropdown.vue';
import TooltipInfo from './../../pge-components/pge-info-tooltip.vue';
import FormInput from './../../pge-components/pge-input-text.vue';
import PgeSwitch from './../../pge-components/pge-switch.vue';
import { usePolicyLinesStore } from '../../stores/policyLines';

const store = window.store;

// The Policy type field's choices, so the builder can show what it will ask
// and say so plainly when a hub has not defined any yet.
const policyLines = usePolicyLinesStore();
const isSettingsShown = ref(false);
const modalErase = ref({ visible: false });
const isLoading = ref(true);
const groups = ref([]);
const formData = reactive({
  access_management_enabled: false,
});
const documentLinks = ref([]);
const settingsEl = ref(null);
const eventLogo = ref('');
const groupTransitionName = ref('');
const fieldTransitionName = ref('');
const isFormChanged = ref(false);
const formHasError = ref(false);
const countries = ref(false);
const regions = ref(false);
let clonedGroups = [];
let clonedDocuments = [];
let clonedAccessEnabled = false;
// A hub with no registration fields is shown one blank section to start from;
// until somebody fills it in, it is not a form and must not be saved as one.
let hadNoFields = false;
const envURLs = inject('envURLs');

const getData = async () => {
  try {
    const res = await axios.get(`${store.event_backend_api}/register-settings`);
    if (res.data) {
      const {
        fields,
        links,
        logo,
      } = res.data.data.settings;

      const {
        countries: _countries,
        regions: _regions,
      } = res.data.data;

      countries.value = _countries;
      regions.value = _regions;

      formData.access_management_enabled = res.data.data.settings.access_management_enabled ?? false;
      clonedAccessEnabled = formData.access_management_enabled;
      documentLinks.value = links;
      eventLogo.value = logo;

      clonedDocuments = _.cloneDeep(links);
      hadNoFields = !fields.length;

      if (fields.length) {
        groups.value = fields.map((group, groupIndex) => {
          if (group.fields) {
            group.fields = group.fields.map((field, fieldIndex) => {
              field.settings = false;
              field.focused = false;
              if (!('required' in field)) field.required = false;

              if (field.new) delete field.new;
              field.id = fieldIndex;

              return field;
            });
          } else {
            group.fields = [];
          }

          if (group.new) delete group.new;

          group.settings = false;
          group.focused = false;
          group.id = groupIndex;
          return group;
        });
      } else {
        groups.value = [{
          id: 0,
          title: '',
          settings: false,
          focused: false,
          fields: [{
            id: 0,
            type: 'text',
            title: '',
            settings: false,
            focused: false,
            required: false
          }]
        }];
      }

      clonedGroups = _.cloneDeep(groups.value);
    }
  } catch (error) {
    console.error(error);
  }
};

const addGroup = () => {
  groups.value.push({
    id: groups.value.length ? groups.value[groups.value.length - 1].id + 1 : 0,
    title: '',
    fields: [{
      id: 0,
      type: 'text',
      title: '',
    }]
  });

  handleRegistrationHistory();
};

const removeGroup = (data) => {
  const { groupIndex } = data;

  // Safety net: the registration form must always keep at least one section,
  // otherwise it lands in an unsavable state (see handleDeleteModal).
  if (groups.value.length <= 1) {
    modalErase.value = { visible: false };
    return;
  }

  groups.value = groups.value.filter((item, index) => index !== groupIndex);

  modalErase.value = { visible: false };
  handleRegistrationHistory();
};

const addGroupField = (groupIndex) => {
  groups.value[groupIndex].fields.push({
    id: groups.value[groupIndex].fields.length ? groups.value[groupIndex].fields[groups.value[groupIndex].fields.length - 1].id + 1 : 0,
    type: 'text',
    title: ''
  });

  handleRegistrationHistory();
};

const removeGroupField = (data) => {
  const { groupIndex, fieldIndex } = data;

  if (groups.value[groupIndex]) {
    groups.value[groupIndex].fields = groups.value[groupIndex].fields.filter((item, index) => index !== fieldIndex);
  }

  modalErase.value = { visible: false };
  handleRegistrationHistory();
};

const handleSave = async () => {
  const builderEdited = isBuilderEdited();

  try {
    let postData = builderEdited || !hadNoFields ? _.cloneDeep(groups.value) : [];

    if (postData.length) {
      postData = postData.map((group, groupIndex) => {
        group.fields = group.fields.map((field, fieldIndex) => {
          field.name = field.title.toLowerCase().replaceAll(' ', '_');
          if (field.new) delete field.new;
          if ('settings' in field) delete field.settings;
          if ('focused' in field) delete field.focused;
          if ('id' in field) delete field.id;
          if (field.type === 'custom' && field.options)
            field.options = field.options.split(',').filter(option => option.replace(/\s/g, '') !== '').join(',');

          if (field.title === '') {
            groups.value[groupIndex].fields[fieldIndex].focused = true;
          } else {
            groups.value[groupIndex].fields[fieldIndex].focused = false;
          }

          return field;
        });

        if (group.new) delete group.new;
        if ('settings' in group) delete group.settings;
        if ('focused' in group) delete group.focused;
        if ('id' in group) delete group.id;
        if (group.title === '') {
          groups.value[groupIndex].focused = true;
        } else {
          groups.value[groupIndex].focused = false;
        }
        return group;
      });
    }

    if (builderEdited && validateFields(postData)) {
      formHasError.value = true;
      window.loadNotification({ message: 'Registration form fields are required!', error: true });
      return;
    }

    const res = await axios.put(`${store.event_backend_api}/register-settings-update`,
      {
        register_fields: postData,
        login_links: documentLinks.value,
        access_management_enabled: formData.access_management_enabled,
      }
    );
    if (res.data.message) {
      formHasError.value = false;
      isFormChanged.value = false;
      store.historyChanges = [];
      window.loadNotification({ message: 'Settings updated successfully' });
      clonedGroups = _.cloneDeep(groups.value);
      clonedDocuments = _.cloneDeep(documentLinks.value);
      clonedAccessEnabled = formData.access_management_enabled;
      hadNoFields = !postData.length;
    }
  } catch (error) {
    console.error(error);
  }
};

const validateFields = (data) => {
  const titles = data.filter(item => !`${item.title}`.replace(/ /g, ''));
  const fields = data.filter(item =>
    !!item.fields.filter(
      field => !field.type ||
        !field.title ||
        (field.type === 'custom' &&
          !field.options
        )).length);

  return titles.length + fields.length;
};

const handleModalClose = () => {
  modalErase.value = { visible: false };
};

const handleTypeChange = (groupIndex, fieldIndex) => {
  // `policy_line` deliberately falls into the else branch: its choices live on
  // the Policy lines page, so copying them into `options` here is exactly the
  // drift this field type exists to end.
  if (groups.value[groupIndex].fields[fieldIndex].type === 'custom') {
    groups.value[groupIndex].fields[fieldIndex].options = 'President,Software Developer,HR,Manager,Project Manager,Other';
  } else {
    if ('options' in groups.value[groupIndex].fields[fieldIndex]) {
      delete groups.value[groupIndex].fields[fieldIndex].options;
    }
  }

  handleHistoryChanges('registration');
};

const handleFieldMouseOver = async (groupIndex, fieldIndex) => {
  await nextTick();
  if (groups.value[groupIndex].fields[fieldIndex]) {
    groups.value[groupIndex].fields[fieldIndex].settings = true;
  }
};

const handleFieldMouseLeave = async (groupIndex, fieldIndex) => {
  await nextTick();
  groups.value[groupIndex].fields[fieldIndex].settings = false;
  isSettingsShown.value = false;
};

const handleTitleMouseOver = async (index) => {
  await nextTick();
  if (groups.value[index]) {
    groups.value[index].settings = true;
  }
};

const handleTitleMouseLeave = async (index) => {
  await nextTick();
  groups.value[index].settings = false;
  isSettingsShown.value = false;
};

const isBuilderEdited = () => {
  const current = _.cloneDeep(groups.value).map(group => {
    if (group.fields) {
      group.fields = group.fields.map(field => {
        field.settings = false;
        field.focused = false;
        return field;
      });
    } else {
      group.fields = [];
    }

    group.settings = false;
    group.focused = false;
    return group;
  });

  return deepArrObjDiff(clonedGroups, current).length > 0;
};

const recordChange = (type, changed) => {
  const index = store.historyChanges.indexOf(type);

  if (changed && index === -1) store.historyChanges.push(type);
  if (!changed && index !== -1) store.historyChanges.splice(index, 1);

  isFormChanged.value = store.historyChanges.length > 0;
};

const handleRegistrationHistory = () => recordChange('registration', isBuilderEdited());

const handleLoginHistory = () => recordChange('login', deepArrObjDiff(clonedDocuments, _.cloneDeep(documentLinks.value)).length > 0);

const handleAccessHistory = () => recordChange('access', formData.access_management_enabled !== clonedAccessEnabled);

const validateRegistrationForm = () => {
  for (let i = 0; i < groups.value.length; i++) {
    if (!groups.value[i].title.replace(/ /g, '')) {
      return false;
    }
    for (let j = 0; j < groups.value[i].fields.length; j++) {
      if (!groups.value[i].fields[j].title.replace(/ /g, '')) {
        return false;
      }

      if (groups.value[i].fields[j].hasOwnProperty('options') &&
        !groups.value[i].fields[j].options.replace(/ /g, '')
      ) {
        return false;
      }
    }
  }

  return true;
};

const handleHistoryChanges = async (type) => {
  await nextTick();
  if (type === 'registration') {
    const validated = !isBuilderEdited() || validateRegistrationForm();
    if (!validated) {
      formHasError.value = true;
      return;
    } else {
      formHasError.value = false;
    }
  }

  const historyHandlers = {
    'registration': handleRegistrationHistory,
    'login': handleLoginHistory,
    'access': handleAccessHistory,
  };
  const handler = historyHandlers[type];
  if (handler) {
    handler();
  }
};

const handleDeleteModal = (groupIndex, fieldIndex = null) => {
  // A registration form must keep at least one section. Block deleting the last
  // remaining section and explain why, instead of leaving the form in an invalid,
  // unsavable state with a silently disabled Save button.
  if (fieldIndex === null && groups.value.length <= 1) {
    window.loadNotification({
      message: 'Your registration form must have at least one section. Add another section before deleting this one.',
      error: true,
    });
    return;
  }

  modalErase.value.visible = true;
  modalErase.value.groupIndex = groupIndex;
  if (fieldIndex !== null) {
    modalErase.value.fieldIndex = fieldIndex;
  }
};

const handleRegistrationFocus = (focused, groupIndex, fieldIndex = null) => {
  if (fieldIndex !== null) {
    groups.value[groupIndex].fields[fieldIndex].focused = focused;
    return;
  }
  groups.value[groupIndex].focused = focused;
};

const handleRemoveLink = (index) => {
  documentLinks.value = documentLinks.value.filter((_, idx) => idx !== index);
  handleLoginHistory();
};

const swapGroups = async (index, down) => {
  const tmp = down ? 1 : -1;
  if (groups.value[index + tmp]) {
    // Nested move transitions will not work as expected.
    // When parent transition is triggered, the child also gets triggered.
    // So we need to reset the transition name to enable the animation and then re-disable it.
    groupTransitionName.value = 'swap-400';
    [groups.value[index], groups.value[index + tmp]] = [groups.value[index + tmp], groups.value[index]];
    // Wait for the next tick to allow Vue to update the DOM before re-disabling the transition.
    await nextTick();
    groupTransitionName.value = '';
    handleHistoryChanges('registration');
  }
};

const swapFields = async (groupIndex, fieldIndex, down) => {
  const tmp = down ? 1 : -1;
  if (groups.value[groupIndex].fields[fieldIndex + tmp]) {
    fieldTransitionName.value = 'swap-400';
    [groups.value[groupIndex].fields[fieldIndex], groups.value[groupIndex].fields[fieldIndex + tmp]] = [groups.value[groupIndex].fields[fieldIndex + tmp], groups.value[groupIndex].fields[fieldIndex]];
    await nextTick();
    fieldTransitionName.value = '';
    handleHistoryChanges('registration');
  }
};

const handlePdfUpload = (data) => {
  documentLinks.value.push({
    title: data.post_title,
    value: data.url
  });

  handleLoginHistory();
};

const handleDeletePdf = (data) => {
  documentLinks.value = documentLinks.value.filter(document => document.value !== data.url);

  handleLoginHistory();
};

onMounted(async () => {
  policyLines.load();
  await getData();
  isLoading.value = false;
});
</script>

<template>
  <PageHeader>
    <template #left>
      <form-button label="Save" :disabled="!isFormChanged || formHasError" @click="handleSave" />
    </template>
  </PageHeader>
  <div class="page-container">
    <modal v-if="modalErase.visible"
      :title="modalErase.fieldIndex !== undefined ? 'Delete Section Field' : 'Delete Section'"
      :on-close="handleModalClose" max-width="360px">
      <template #content>
        <p>{{ modalErase.fieldIndex !== undefined ? 'Do you want to delete this section field?' : 'Do you want to delete this section ?' }}</p>
      </template>
      <template #actions>
        <form-button @click="handleModalClose" template="outlined" label="Cancel" />
        <form-button :disabled="modalErase.loading"
          @click="modalErase.fieldIndex !== undefined ? removeGroupField(modalErase) : removeGroup(modalErase)"
          label="Delete" />
      </template>
    </modal>

    <loading v-if="isLoading" variant="form" :rows="5" />
    <template v-else>
      <div class="page-container__content">
        <div class="page-container__content__reg-container block-sticky">
          <div class="access-management">
            <div class="access-management__heading-row">
              <h2 class="access-management__title">Access Management</h2>
            </div>
            <p class="access-management__helper">When enabled, only invited policyholders can register and access this Hub.</p>
            <div class="access-management__toggle-row">
              <PgeSwitch
                v-model="formData.access_management_enabled"
                name="access_management_enabled"
                label=""
                @change="handleHistoryChanges('access')"
              />
              <span class="access-management__toggle-label">Enable only invited policyholders</span>
            </div>
            <ul class="access-management__hints">
              <li>Only invited users can register and access this Hub.</li>
              <li>Blocked users see a validation message with a link to Request Access.</li>
              <li>Manage invited policyholders and review access requests under Policyholders.</li>
            </ul>
          </div>
          <div class="page-container__content__registration">
            <div class="register-fields">
              <div class="fields-header__sm">
                <h2>Registration Form Builder</h2>
                <hr />
                <div class="fields-header__subtitles">
                  <span>Invitees will fill out this form to register for your event. See right for preview.</span>
                  <tooltip-info
                    label="Form fields will create filterable categories in OmniChat."
                    custom
                    x-position="right"
                    title="Registration Data to OmniChat Filters"
                    text="OmniChat lets policyholders message and network hub-wide. Policyholders can filter participants by their answers to registration questions to find and connect with others."
                  />
                </div>
              </div>
              <PersonalDetails />
              <transition-group :name="groupTransitionName">
                <template v-for="(group, groupIndex) in groups" :key="group.id">
                  <div class="container">
                    <div @mouseover="handleTitleMouseOver(groupIndex)" @mouseleave="handleTitleMouseLeave(groupIndex)"
                      class="group-title flex">
                      <div class="draggable-container group-title--left handle">
                        <span role="button" @click="swapGroups(groupIndex, false)">
                          <icon-caret-up color="#999999" />
                        </span>
                        <span role="button" @click="swapGroups(groupIndex, true)">
                          <icon-caret-down color="#999999" />
                        </span>
                      </div>
                      <div class="group-title--right" :class="`${groupIndex}.title`" style="background-color: #ffffff;">
                        <form-input class="title-1" :name="`${groupIndex}.title`" :model="group.title"
                          v-model="group.title" required :max-length="200" :focused="group.focused" label="Section Name"
                          :should-show-error="formHasError && !group.title"
                          @input="handleHistoryChanges('registration')"
                          @update:focused="(focused) => handleRegistrationFocus(focused, groupIndex)" />
                      </div>
                      <div class="settings-container flex-start">
                        <div v-if="group.settings">
                          <button ref="settingsEl" class="settings__button" @click="isSettingsShown = !isSettingsShown">
                            <icon-ellipsis />
                          </button>
                          <dropdown :is-visible="isSettingsShown" position="auto-right"
                            :rect="settingsEl && settingsEl[0] ? settingsEl[0].getBoundingClientRect() : null"
                            has-footer style="width: 280px; padding: 0;">
                            <div class="dropdown-container">
                              <div class="delete-container" @click="handleDeleteModal(groupIndex)">
                                <icon-trash color="#B3261E" />
                                <span>Delete Section</span>
                              </div>
                            </div>
                          </dropdown>
                        </div>
                      </div>
                    </div>
                    <div class="padding-left margin-top">
                      <transition-group :name="fieldTransitionName">
                        <template v-for="(field, fieldIndex) in group.fields" :key="field.id">
                          <div class="row">
                            <div class="column column--full">
                              <div class="row"
                                style="align-items: center; justify-content: center; margin-bottom: 20px;"
                                @mouseover="handleFieldMouseOver(groupIndex, fieldIndex)"
                                @mouseleave="handleFieldMouseLeave(groupIndex, fieldIndex)">
                                <div class="draggable-container handle-3">
                                  <span role="button" @click="swapFields(groupIndex, fieldIndex, false)">
                                    <icon-caret-up color="#999999" />
                                  </span>
                                  <span role="button" @click="swapFields(groupIndex, fieldIndex, true)">
                                    <icon-caret-down color="#999999" />
                                  </span>
                                </div>
                                <div class="column" style="width: 70%; background-color: #ffffff">
                                  <form-input :name="`${groupIndex}.${fieldIndex}.title`" :model="field.title"
                                    v-model="field.title" :focused="field.focused" label="Field Name" required
                                    :max-length="200" :should-show-error="formHasError && !field.title" class="title-2"
                                    @input="handleHistoryChanges('registration')"
                                    @update:focused="(focused) => handleRegistrationFocus(focused, groupIndex, fieldIndex)" />
                                </div>
                                <div class="flex-start" style="width: 30%; background-color: #ffffff;">
                                  <dropdown-menu :name="`${groupIndex}.${fieldIndex}.type`" :model-value="field.type"
                                    v-model="field.type" label="Field Type" :options="[
                                      { value: 'text', label: 'Text', info: 'Text Field' },
                                      { value: 'custom', label: 'Custom List', info: 'Dropdown Field' },
                                      { value: 'policy_line', label: 'Policy type', info: 'From your Policy lines' },
                                      { value: 'country', label: 'Country', info: 'Dropdown Field' },
                                      { value: 'state', label: 'State / Province', info: 'Dropdown Field' },
                                    ]" style="margin: 0;" @change="handleTypeChange(groupIndex, fieldIndex)" />
                                </div>
                                <div class="settings-container flex-start">
                                  <div v-if="field.settings">
                                    <button ref="settingsEl" class="settings__button"
                                      @click="isSettingsShown = !isSettingsShown">
                                      <icon-ellipsis />
                                    </button>
                                    <dropdown :is-visible="isSettingsShown" position="auto-right"
                                      :rect="settingsEl && settingsEl[0] ? settingsEl[0].getBoundingClientRect() : null"
                                      has-footer style="width: 280px; padding: 0;">
                                      <div class="dropdown-container">
                                        <div class="delete-container"
                                          @click="handleDeleteModal(groupIndex, fieldIndex)">
                                          <icon-trash color="#B3261E" />
                                          <span>Delete Field</span>
                                        </div>
                                      </div>
                                    </dropdown>
                                  </div>
                                </div>
                              </div>
                              <div v-if="field.type === 'policy_line'" class="row padding-left" style="padding-right: 35px;">
                                <div class="column policy-line-note">
                                  <p v-if="policyLines.options.length">
                                    The choices come from your
                                    <router-link to="/policy-lines">Policy lines</router-link>, so they
                                    can't drift out of step:
                                    <b>{{ policyLines.options.map(line => line.name).join(', ') }}</b>.
                                  </p>
                                  <p v-else class="policy-line-note--empty">
                                    You have no policy lines yet, so this question has nothing to offer.
                                    Add one on <router-link to="/policy-lines">Policy lines</router-link>
                                    first.
                                  </p>
                                </div>
                              </div>
                              <div v-if="field.type === 'custom'" class="row padding-left" style="padding-right: 35px;">
                                <div class="column" style="background-color: #ffffff;">
                                  <form-input :name="`${groupIndex}.${fieldIndex}.options`" :model="field.options"
                                    v-model="field.options" label="Custom Dropdown Items" required
                                    info="Separate items with a comma" @input="handleHistoryChanges('registration')" />
                                </div>
                              </div>
                              <div class="padding-left-md checkboxes-container" style="margin-top: 30px;">
                                <form-input-old type="checkbox" :name="`${groupIndex}.${fieldIndex}.required`"
                                  :model="field.required" v-model="field.required" label="Required Field"
                                  info="Participants must enter this field to register" label-padding="padding-left-md"
                                  @change="handleHistoryChanges('registration')" />

                              </div>
                            </div>
                          </div>
                        </template>
                      </transition-group>
                    </div>

                    <div class="flex field-button">
                      <form-button template="outlined" label="Add Field" icon="Plus" size="18" button-size="small"
                        @click="addGroupField(groupIndex)" />
                    </div>
                  </div>
                </template>
              </transition-group>

              <div class="registration-footer mt-30">
                <hr />
                <div>
                  <form-button label="Add Section" template="tonal" icon="SectionAdd" button-size="small"
                    @click="addGroup" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="block-sticky">
          <registration :countries="countries" :regions="regions" :policy-lines="policyLines.options"
            :logo="eventLogo" :event-name="store.subdomainName" :groups="groups" />
        </div>
      </div>

      <div class="page-container__content">
        <div class="page-container__content__login block-sticky">
          <div class="fields-header">
            <h2>Login & Documents</h2>
            <hr />
            <div class="fields-header__subtitles">
              <span>These documents will be viewable for policyholders when they log in to your hub.</span>
            </div>
          </div>
          <div class="form-documents">
            <div class="form-documents__input required">
              <div>
                <span class="form-documents__label">Required Document</span>
                <div>
                  <p>XtendLive Attendee Terms of Service Agreement</p>
                </div>
              </div>
              <a :href="envURLs.eco + '/docs/attendee-terms-of-use.pdf'" target="_blank" rel="noopener noreferrer">
                <icon-eye-open />
                <span>Preview</span>
              </a>
            </div>
            <div v-for="(link, index) in documentLinks" :key="index" class="form-documents__input">
              <div>
                <span class="form-documents__label">Document</span>
                <div>
                  <p class="truncate">{{ link.title }}</p>
                  <button @click="handleRemoveLink(index)">
                    <icon-close />
                  </button>
                </div>
              </div>
              <a :href="link.value" target="_blank" rel="noopener noreferrer">
                <icon-eye-open />
                <span>Preview</span>
              </a>
            </div>

            <div>
              <form-input-old name="media-pdf" type="file" labelText="Select a Document" accept="application/pdf"
                mode="compact" icon="Media" template="outlined" iconSize="18" label="Add New Document" upload
                style="margin-bottom: 0;" @change="handlePdfUpload" @delete-pdf="handleDeletePdf" tooltip="20 MB max per upload" />
            </div>
          </div>
        </div>

        <div class="block-sticky">
          <login :logo="eventLogo" :links="documentLinks" />
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.policy-line-note {
  p { margin: 0; font-size: 13px; line-height: 1.5; color: #4b444d; }

  a { color: #6750a4; text-decoration: underline; }

  &--empty { color: #b3261e !important; }
}

.page-container {
  // margin-top: 56px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  row-gap: var(--setup-card-gap);
  height: 100%;
  padding: var(--setup-page-padding) var(--setup-page-padding) 0 var(--setup-page-padding);
  margin-bottom: 70px;

  hr {
    display: block;
    height: 1px;
    border: 0 !important;
    border-top: 1px solid rgba(128, 128, 128, 1) !important;
    margin-bottom: 10px;
    padding: 0;
  }

  .padding-left {
    padding-left: 50px;
  }

  .padding-left-sm {
    padding-left: 30px;
  }

  .padding-left-md {
    padding-left: 70px;
  }
  .checkboxes-container {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
  .flex-start {
    align-self: flex-start;
  }

  .block-sticky {
    align-self: flex-start;
    position: -webkit-sticky; 
    position: sticky;
    top: 56px;
  }

  .fields-header {
    margin-bottom: 40px;

    &__sm {
      margin-bottom: 18px;
    }

    span {
      font-size: 12px;
      line-height: 16px;
    }

    &__subtitles {
      display: flex;
      flex-direction: column;
      gap: 7px;
      margin-bottom: 14px;

      span {
        font-size: 12px;
        line-height: 16px;
      }
    }
  }

  .draggable-container {
    display: flex;
    flex-direction: column;
    align-self: flex-start;
    margin-top: -8px;
  }

  .draggable-container>span {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 100%;
    width: 36px;
    height: 36px;
    cursor: pointer;
  }

  .draggable-container>span:hover {
    background-color: #1d1b2014;
  }

  .draggable-container>span:active {
    background-color: #1d1b201f;
    transition: background-color 150ms linear;
  }

  .draggable-container>span:not(:active) {
    transition: background-color 150ms linear;
  }

  .settings-container {
    position: relative;
    width: 22px;
    margin-top: 12px;

    .dropdown-container {
      width: 100%;
      display: flex;
      flex-direction: column;

      button {
        display: flex;
        column-gap: 10px;
        padding: 12px;
        background-color: transparent;
        border: none;
        outline: none;

        span {
          font-size: 16px;
        }
      }
    }

    .input-container {
      padding: 12px 4px 0 4px;
      transition: background-color 250ms linear;
    }

    .input-container:hover {
      background-color: #5E2F7814;
    }

    .delete-container {
      display: flex;
      column-gap: 16px;
      align-items: center;
      padding: 22px 14px;
      color: #B3261E;
      cursor: pointer;
      transition: background-color 250ms linear;
    }

    .delete-container:hover {
      background-color: #BA1A1A14;
    }
  }

  &__content {
    width: 100%;
    display: flex;
    column-gap: var(--setup-card-gap);

    &:first-child {
      z-index: 1;
    }

    &__reg-container {
      display: flex;
      flex-direction: column;
      row-gap: var(--setup-card-gap);
      width: 100%;

      .access-management {
        background-color: #ffffff;
        border-radius: 12px;
        padding: 28px 32px;
        width: 100%;
        box-sizing: border-box;

        &__heading-row {
          padding-bottom: 12px;
          margin-bottom: 16px;
          border-bottom: 1px solid #E5E5E5;
        }

        &__title {
          font-size: 22px;
          font-weight: 600;
          color: #1E1A1F;
          margin: 0;
          line-height: 1.3;
        }

        &__helper {
          font-size: 14px;
          color: #4B444D;
          margin: 0 0 20px 0;
          line-height: 1.5;
        }

        &__toggle-row {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
        }

        &__toggle-label {
          font-size: 16px;
          color: #1E1A1F;
          line-height: 1.4;
        }

        &__hints {
          margin: 0;
          padding-left: 20px;
          list-style-type: disc;

          li {
            font-size: 14px;
            line-height: 1.8;
            color: #1E1A1F;
          }
        }
      }
    }

    &__registration,
    &__login {
      background-color: #ffffff;
      border-radius: 12px;
      padding: 24px 24px 24px 24px;
      min-width: 0;
      width: 100%;

      h2 {
        font-weight: 700;
        font-size: 20px;
        line-height: 24px;
        margin: 0;
      }

      .settings__button {
        background-color: transparent;
        border: none;
        padding: 4px 6px;
        border-radius: 9999px;
        cursor: pointer;
      }

      .settings__button:hover {
        background-color: #1d1b2014;
        transition: background-color 150ms linear;
      }

      .settings__button:active {
        background-color: #1d1b201f;
        transition: background-color 150ms linear;
      }

      .settings__button:not(:active) {
        transition: background-color 150ms linear;
      }

      .container {
        >.row {
          padding: 15px;
          margin-bottom: 0 !important;
        }

        .row {
          display: flex !important;
          justify-content: space-between;
          align-items: center;
          width: auto;

          .form-button {
            margin: 0;

            button {
              padding: 0;
              border: 0;
            }
          }

          .column {
            &--align-bottom {
              display: flex;
              align-items: flex-end;
            }

            &--mt-minus-50 {
              margin-top: -43px !important;
            }

            &--mt-minus-70 {
              margin-top: -68px !important;
            }

            &--mt-minus-145 {
              margin-top: -145px !important;
            }

            &--mt-minus-170 {
              margin-top: -170px !important;
            }
          }

          .row {
            .column {
              flex: 1;
              align-self: flex-start;
            }
          }
        }

        .field-button {
          justify-content: center;
          margin: 20px 0 40px 0;
        }
      }

      .group-title {
        margin-bottom: 30px;

        &--right {
          display: flex;
          align-items: center;
          column-gap: 15px;
          width: 100%;
          border-radius: 5px;
          flex: 1;
        }

        &--left {
          background-color: transparent;
        }

        .form-input {
          background-color: transparent;
          flex: 1;
          width: 100%;
        }
      }

      .registration-footer {
        display: flex;
        flex-direction: column;
        row-gap: 14px;
        margin: 30px 40px 0 65px;

        hr {
          width: 100%;
          border-top: 1px solid #ABA4B2 !important;
        }
      }
    }

    &__login {
      z-index: 1;

      .form-documents {
        display: flex;
        flex-direction: column;
        row-gap: 40px;

        &__input.required {
          div {
            opacity: 0.4;
            height: 54px;
          }

          div:first-child {
            min-width: 0;
          }
        }

        &__input {
          display: flex;
          flex-direction: column;
          row-gap: 14px;
          position: relative;
          width: 100%;

          div {
            .form-documents__label {
              background-color: #ffffff;
              color: #49454F;
              position: absolute;
              top: -10px;
              left: 10px;
              display: inline-block;
              width: auto;
              font-style: normal;
              font-weight: 400;
              font-size: 12px;
              line-height: 20px;
              padding: 0 5px;
              z-index: 50;
              cursor: default;
            }

            div {
              display: flex;
              justify-content: space-between;
              align-items: center;
              border: 1px solid #79747E;
              border-radius: 5px;
              width: 100%;
              padding: 14px;

              p {
                margin: 0;
              }

              button {
                padding: 0;
                border: none;
                background-color: transparent;
                height: 24px;
                width: 24px;
                cursor: pointer;
              }
            }
          }

          a {
            display: flex;
            align-items: center;
            width: fit-content;
            column-gap: 8px;
            color: #000;
            text-decoration: none;
            font-size: 14px;
            margin-left: 10px;

            svg {
              height: 18px;
              width: 18px;
            }
          }
        }
      }
    }
  }
}
</style>
